import React, {useState} from "react";
import {useIntl} from "react-intl";
import "./TopicDescriptionCreation.css";
import {Resolution} from "../../../logic/API";
import {
  AuthenticationBasic,
  AuthenticationToken,
  AuthMethod,
  TopicDescription,
} from "../../../logic/APITypes";
import eye from "../../../images/eye.svg";
import {RadioButtonGroup, TextInput} from "@spg/component_library";
import {DefaultInputBehavior} from "../../../reusables/DefaultInputBehavior";

/**
 * Displays the part of the transmission section that accepts authentication credentials when creating, viewing and editing topic descriptions.
 */
function AuthenticationSubSection(props: {
  alternative: "create" | "view" | "edit";
  waitingForResponse: boolean;
  authentication: {get: AuthMethod; set: (authentication: AuthMethod) => void};
  name: {get: string; set: (name: string) => void};
  secret: {get: string; set: (secret: string) => void};
  initialTopic: Resolution<TopicDescription> | undefined;
}) {
  const intl = useIntl();
  const [showSecret, setShowSecret] = useState(false);

  const initialTopic = props.initialTopic?.payload;
  const placeholder = props.initialTopic?.unresolved
    ? intl.formatMessage({
        id: "topicDescriptionCreation.inputLabel.placeholder.loading",
      })
    : undefined;

  return (
    <>
      <RadioButtonGroup
        label={intl.formatMessage({
          id: "topicDescriptionCreation.push.information.radioHeader",
        })}
        idBase={"authentication"}
        name={"authentication"}
        options={{
          NULL: intl.formatMessage({
            id: "topicDescriptionCreation.option.authentication.null",
          }),
          BASIC: intl.formatMessage({
            id: "topicDescriptionCreation.option.authentication.basic",
          }),
          TOKEN_HEADER: intl.formatMessage({
            id: "topicDescriptionCreation.option.authentication.token.header",
          }),
          TOKEN_QUERY: intl.formatMessage({
            id: "topicDescriptionCreation.option.authentication.token.query",
          }),
        }}
        value={props.authentication.get}
        setValue={(newValue: string | null) => {
          if (
            newValue === null ||
            !["NULL", "BASIC", "TOKEN_HEADER", "TOKEN_QUERY"].includes(newValue)
          ) {
            return;
          }
          props.authentication.set(newValue as AuthMethod);
          props.name.set("");
          props.secret.set("");
        }}
        isReadOnly={props.alternative === "view"}
      />
      <div className="topicDescriptionCreationAuthNameContainer">
        <DefaultInputBehavior
          component={TextInput}
          label={intl.formatMessage({
            id:
              props.authentication.get === "BASIC"
                ? "topicDescriptionCreation.inputLabel.username"
                : "topicDescriptionCreation.inputLabel.tokenname",
          })}
          placeholder={placeholder || ""}
          idBase={"topicDescriptionCreationUsername"}
          value={props.name.get}
          setValue={props.name.set}
          isRequired={props.authentication.get !== "NULL"}
          isDisabled={
            props.waitingForResponse || props.authentication.get === "NULL"
          }
          isReadOnly={
            props.alternative === "view" || props.initialTopic?.unresolved
          }
          isEdited={
            initialTopic &&
            initialTopic.transfer.direction === "PULL" &&
            props.name.get !==
              (initialTopic.transfer.authentication?.method === "BASIC"
                ? (initialTopic.transfer.authentication as AuthenticationBasic)
                    ?.username
                : (initialTopic.transfer.authentication as AuthenticationToken)
                    ?.name)
          }
        />
      </div>
      <div className="topicDescriptionCreationSecretContainer">
        <DefaultInputBehavior
          component={TextInput}
          label={intl.formatMessage({
            id:
              props.authentication.get === "BASIC"
                ? "topicDescriptionCreation.inputLabel.password"
                : "topicDescriptionCreation.inputLabel.token",
          })}
          placeholder={placeholder || ""}
          idBase={"topicDescriptionCreationSecret"}
          value={props.secret.get}
          setValue={props.secret.set}
          isPassword={props.alternative === "view" || !showSecret}
          isRequired={props.authentication.get !== "NULL"}
          trailingIconButton={{
            iconProps: {
              title: intl.formatMessage({
                id:
                  props.authentication.get === "BASIC"
                    ? "topicDescriptionCreation.iconButton.showPassword"
                    : "topicDescriptionCreation.iconButton.showToken",
              }),
              href:
                eye +
                (props.alternative === "view" || showSecret
                  ? "#crossedOut"
                  : "#globalG"),
              strokeColor: "black",
            },
            idBase: "showPassword",
            onClick: () => {
              setShowSecret(!showSecret);
            },
          }}
          isDisabled={
            props.waitingForResponse || props.authentication.get === "NULL"
          }
          isReadOnly={
            props.alternative === "view" || props.initialTopic?.unresolved
          }
          isEdited={
            initialTopic &&
            initialTopic.transfer.direction === "PULL" &&
            props.secret.get !==
              (initialTopic.transfer.authentication?.method === "BASIC"
                ? (initialTopic.transfer.authentication as AuthenticationBasic)
                    ?.password
                : (initialTopic.transfer.authentication as AuthenticationToken)
                    ?.token)
          }
        />
      </div>
    </>
  );
}

export {AuthenticationSubSection};
